// Donation dashboard — everything is scoped to the session's legal entity + operation unit +
// department (see session-scope.tsx), an optional cause filter (?cause=) and one financial year
// (defaults to the org's current donation FY).

import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  HandHeart,
  Minus,
  ReceiptText,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useAuth } from "@/lib/auth";
import {
  fetchDashboard,
  fetchInsights,
  INSIGHT_SECTIONS,
  type InsightSection,
  type Insights,
  formatAmount,
  formatAmountCompact,
  type Breakdown,
  type Dashboard as DashboardData,
  type MonthRow,
  type Totals,
} from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/page";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { CauseFilter, useCauseParam } from "@/components/cause-filter";
import { DonorLink } from "@/components/donor-link";
import { DonorSheet } from "@/components/donor-sheet";
import {
  DonorTiles,
  DonationSizeCard,
  OperationsCard,
  PipelineCard,
  UpcomingAnniversariesCard,
} from "@/components/insights";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Dashboard() {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [budgetYearId, setBudgetYearId] = useState<number | null>(null);
  const [causeId] = useCauseParam();
  // Dashboard donors open in a bottom sheet instead of navigating away from the figures.
  // It lives in the URL (?donor=<id>) like the other filters, so it survives a reload and a
  // shared link opens the same quick view.
  const [params, setParams] = useSearchParams();
  const sheetDonorId = Number(params.get("donor")) || null;
  const setSheetDonorId = useCallback(
    (id: number | null) => {
      const q = new URLSearchParams(params);
      if (id) q.set("donor", String(id));
      else q.delete("donor");
      setParams(q, { replace: true });
    },
    [params, setParams]
  );
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !scope) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDashboard(token, scope, { budgetYearId, causeId }, controller.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if ((e as Error).name !== "AbortError") setError((e as Error).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, scope, budgetYearId, causeId]);

  // Donor / size / pipeline / operations counts: four sections requested IN PARALLEL (separate,
  // shorter requests — one slow section can no longer time out the others). Each keeps its own
  // data, loading flag and error, so every block depends only on its own section.
  const [parts, setParts] = useState<Partial<Insights>>({});
  const [partLoading, setPartLoading] = useState<Record<InsightSection, boolean>>({
    donors: true,
    sizes: true,
    pipeline: true,
    operations: true,
  });
  const [partError, setPartError] = useState<Partial<Record<InsightSection, string>>>({});
  const [canViewDetails, setCanViewDetails] = useState(false);
  useEffect(() => {
    if (!token || !scope) return;
    const controller = new AbortController();
    setPartLoading({ donors: true, sizes: true, pipeline: true, operations: true });
    setPartError({});
    INSIGHT_SECTIONS.forEach((section) => {
      fetchInsights(token, scope, { budgetYearId, causeId }, section, controller.signal)
        .then((d) => {
          const { canViewDetails: cv, ...rest } = d;
          setParts((p) => ({ ...p, ...rest }));
          if (cv != null) setCanViewDetails(cv);
        })
        .catch((e) => {
          if ((e as Error).name !== "AbortError") setPartError((m) => ({ ...m, [section]: (e as Error).message }));
        })
        .finally(() => {
          if (!controller.signal.aborted) setPartLoading((l) => ({ ...l, [section]: false }));
        });
    });
    return () => controller.abort();
  }, [token, scope, budgetYearId, causeId]);
  // Blocks only read their own section; this view object just carries them + the permission flag.
  const ins = { ...parts, canViewDetails } as Insights;

  if (!scope) return null;
  const fyId = data?.context.budgetYear.id ?? budgetYearId;

  const fyItems = (data?.budgetYears ?? []).map((y) => ({ value: String(y.id), label: y.name }));

  return (
    <PageContainer full>
      {/* Scope + filter row */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="truncate text-lg font-semibold">{scope.operationUnit.name}</h2>
            <Link to="/select-scope?next=/" className="shrink-0 text-xs text-primary underline-offset-2 hover:underline">
              Change
            </Link>
          </div>
          {scope.department && (
            <p className="truncate text-sm font-medium">Department: {scope.department.name}</p>
          )}
          <p className="truncate text-sm text-muted-foreground">
            {scope.legalEntity.name}
            {data && (
              <>
                {" · "}
                {data.context.isCurrentYear ? "as of " : "FY closed "}
                {formatDate(data.context.asOf)}
              </>
            )}
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <CauseFilter className="min-w-0 flex-1 sm:w-64 sm:flex-none" />
          <div className="w-36">
          <Select
            items={fyItems}
            value={data ? String(data.context.budgetYear.id) : null}
            onValueChange={(v) => v && setBudgetYearId(Number(v))}
            disabled={!data}
          >
            <SelectTrigger className="w-full" aria-label="Financial year">
              <SelectValue placeholder="Financial year" />
            </SelectTrigger>
            <SelectContent>
              {fyItems.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          </div>
        </div>
      </div>

      {/* Two independent sources — money figures (data) and donor/pipeline/ops counts (insights) —
          but EVERY block below shows its own loading state: a placeholder with a spinner on first
          load, and its old content dimmed with its own spinner while it refetches. */}
      {error ? (
        <ErrorCard message={error} />
      ) : !data ? (
        <TilePlaceholders count={6} className="xl:grid-cols-6" />
      ) : (
        <KpiRow data={data} busy={loading} />
      )}

      {partError.donors ? (
        <ErrorCard message={partError.donors} />
      ) : !parts.donors ? (
        <TilePlaceholders count={5} className="xl:grid-cols-5" />
      ) : (
        <DonorTiles ins={ins} fyId={fyId} causeId={causeId} busy={partLoading.donors} />
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {partError.donors ? (
          <ErrorCard message={partError.donors} />
        ) : !parts.donors ? (
          <BlockPlaceholder className="h-64" />
        ) : (
          <UpcomingAnniversariesCard
            ins={ins}
            fyId={fyId}
            causeId={causeId}
            busy={partLoading.donors}
            onOpenDonor={setSheetDonorId}
          />
        )}
        {partError.sizes ? (
          <ErrorCard message={partError.sizes} />
        ) : !parts.donationSizeBands ? (
          <BlockPlaceholder className="h-64" />
        ) : (
          <Busy busy={partLoading.sizes}>
            <DonationSizeCard bands={parts.donationSizeBands} />
          </Busy>
        )}
      </div>

      {!error &&
        (!data ? (
          <BlockPlaceholder className="h-80" />
        ) : (
          <Busy busy={loading}>
            <MonthlyCard data={data} />
          </Busy>
        ))}

      <div className="grid gap-6 lg:grid-cols-2">
        {partError.pipeline ? (
          <ErrorCard message={partError.pipeline} />
        ) : !parts.pipeline ? (
          <BlockPlaceholder className="h-64" />
        ) : (
          <Busy busy={partLoading.pipeline}>
            <PipelineCard ins={ins} fyId={fyId} causeId={causeId} />
          </Busy>
        )}
        {partError.operations ? (
          <ErrorCard message={partError.operations} />
        ) : !parts.operations ? (
          <BlockPlaceholder className="h-64" />
        ) : (
          <Busy busy={partLoading.operations}>
            <OperationsCard ins={ins} fyId={fyId} causeId={causeId} />
          </Busy>
        )}
      </div>

      {!error && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            {!data ? (
              <>
                <BlockPlaceholder className="h-64" />
                <BlockPlaceholder className="h-64" />
              </>
            ) : (
              <>
                {/* Department split only when the session covers all departments. */}
                {!data.context.department && (
                  <Busy busy={loading}>
                    <BreakdownCard title="By department" description="Received this FY, by the cause's department" rows={data.byDepartment} />
                  </Busy>
                )}
                <Busy busy={loading}>
                  <BreakdownCard title="By mode of payment" description="Received this financial year" rows={data.byModeOfPayment} />
                </Busy>
                {/* With one cause selected the per-cause split is meaningless. */}
                {!data.context.cause && (
                  <Busy busy={loading}>
                    <BreakdownCard title="Top causes" description="Top 10 by amount received" rows={data.byOccasion} />
                  </Busy>
                )}
              </>
            )}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {!data ? (
              <>
                <BlockPlaceholder className="h-72" />
                <BlockPlaceholder className="h-72" />
              </>
            ) : (
              <>
                <Busy busy={loading}>
                  <TopDonorsCard rows={data.topDonors} onOpenDonor={setSheetDonorId} />
                </Busy>
                <Busy busy={loading}>
                  <RecentCard data={data} onOpenDonor={setSheetDonorId} />
                </Busy>
              </>
            )}
          </div>
        </>
      )}
      <DonorSheet
        donorId={sheetDonorId}
        budgetYearId={fyId}
        causeId={causeId}
        onClose={() => setSheetDonorId(null)}
      />
    </PageContainer>
  );
}

// ------------------------------------------------------------------ KPI tiles

/** First-load stand-ins for a row of tiles — each tile gets its own spinner. */
function TilePlaceholders({ count, className }: { count: number; className?: string }) {
  return (
    <div className={cn("grid grid-cols-2 gap-3 md:grid-cols-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <BlockPlaceholder key={i} compact className={cn("h-24", i === 0 && "col-span-2 md:col-span-1")} />
      ))}
    </div>
  );
}

function KpiRow({ data, busy }: { data: DashboardData; busy: boolean }) {
  const { kpis, context } = data;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <KpiTile
        busy={busy}
        icon={HandHeart}
        label="Received this FY"
        value={formatAmount(kpis.fy.amount)}
        sub={`${kpis.fy.count} donations`}
        footer={<YoY current={kpis.fy} prev={kpis.prevFyToDate} prevName={context.prevBudgetYear?.name} />}
        className="col-span-2 md:col-span-1"
      />
      <KpiTile
        busy={busy}
        icon={CalendarDays}
        label={context.isCurrentYear ? "This month" : "Last month of FY"}
        value={formatAmount(kpis.month.amount)}
        sub={`${kpis.month.count} donations`}
      />
      {kpis.today ? (
        <KpiTile
        busy={busy}
          icon={ReceiptText}
          label="Today"
          value={formatAmount(kpis.today.amount)}
          sub={`${kpis.today.count} donations`}
        />
      ) : (
        <KpiTile
        busy={busy}
          icon={ReceiptText}
          label="Average donation"
          value={formatAmount(kpis.fy.average)}
          sub="per receipt this FY"
        />
      )}
      <KpiTile
        busy={busy}
        icon={Users}
        label="Donors"
        value={kpis.fy.donors.toLocaleString("en-IN")}
        sub={`avg ${formatAmount(kpis.fy.average)} / donation`}
      />
      <KpiTile
        busy={busy}
        icon={Clock}
        label="Pending receipt"
        value={formatAmount(kpis.pending.amount)}
        sub={`${kpis.pending.count} not yet received`}
      />
      <KpiTile
        busy={busy}
        className="col-span-2 md:col-span-1"
        icon={ReceiptText}
        label="80G eligible"
        value={formatAmount(kpis.eightyG)}
        sub={
          kpis.fy.amount > 0
            ? `${Math.round((kpis.eightyG / kpis.fy.amount) * 100)}% of received`
            : "of received"
        }
      />
    </div>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  sub,
  footer,
  className,
  busy = false,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  footer?: React.ReactNode;
  className?: string;
  busy?: boolean;
}) {
  return (
    <Busy busy={busy} compact className={className}>
    <Card size="sm" className="h-full">
      <CardContent className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon className="size-3.5 shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        <div className="truncate text-xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
        {footer}
      </CardContent>
    </Card>
    </Busy>
  );
}

function YoY({ current, prev, prevName }: { current: Totals; prev: Totals | null; prevName?: string }) {
  if (!prev || !prevName) return null;
  if (prev.amount <= 0) {
    return <div className="text-xs text-muted-foreground">No donations in {prevName} to date</div>;
  }
  const pct = ((current.amount - prev.amount) / prev.amount) * 100;
  const rounded = Math.round(pct);
  const Icon = rounded > 0 ? TrendingUp : rounded < 0 ? TrendingDown : Minus;
  return (
    <div
      className={cn(
        "flex items-start gap-1 text-xs",
        rounded > 0 && "text-emerald-700 dark:text-emerald-400",
        rounded < 0 && "text-red-700 dark:text-red-400",
        rounded === 0 && "text-muted-foreground"
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <span>
        {rounded > 0 ? "+" : ""}
        {rounded}% vs {prevName} to date
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ monthly trend

function MonthlyCard({ data }: { data: DashboardData }) {
  const prevName = data.context.prevBudgetYear?.name;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly donations received</CardTitle>
        <CardDescription>
          FY {data.context.budgetYear.name}
          {prevName ? ` compared with ${prevName}` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="chart">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Legend current={data.context.budgetYear.name} prev={prevName} />
            <TabsList>
              <TabsTrigger value="chart">Chart</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="chart">
            <MonthlyChart rows={data.monthly} showPrev={!!prevName} currentName={data.context.budgetYear.name} prevName={prevName} />
          </TabsContent>
          <TabsContent value="table">
            <MonthlyTable rows={data.monthly} prevName={prevName} currentName={data.context.budgetYear.name} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Legend({ current, prev }: { current: string; prev?: string }) {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-sm bg-series-current" /> {current}
      </span>
      {prev && (
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-series-prev" /> {prev}
        </span>
      )}
    </div>
  );
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
}

function MonthlyChart({
  rows,
  showPrev,
  currentName,
  prevName,
}: {
  rows: MonthRow[];
  showPrev: boolean;
  currentName: string;
  prevName?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...rows.map((r) => Math.max(r.amount, showPrev ? r.prevAmount : 0))));
  const ticks = [max, max / 2, 0];
  const h = (v: number) => `${Math.max(0, (v / max) * 100)}%`;

  return (
    <div className="flex gap-2">
      {/* y axis */}
      <div className="flex h-56 w-12 shrink-0 flex-col justify-between pb-5 text-right text-[10px] text-muted-foreground tabular-nums">
        {ticks.map((t) => (
          <span key={t} className="-translate-y-1/2 first:translate-y-0 last:translate-y-0">
            {formatAmountCompact(t)}
          </span>
        ))}
      </div>
      <div className="relative min-w-0 flex-1">
        {/* recessive grid */}
        <div className="pointer-events-none absolute inset-x-0 top-0 bottom-5 flex flex-col justify-between">
          {ticks.map((t) => (
            <div key={t} className="border-t border-border/70" />
          ))}
        </div>
        <div className="relative flex h-56 items-stretch" onMouseLeave={() => setHover(null)}>
          {rows.map((r, i) => {
            const active = hover === i;
            return (
              <div
                key={`${r.year}-${r.month}`}
                className="relative flex min-w-0 flex-1 flex-col"
                onMouseEnter={() => setHover(i)}
                onClick={() => setHover(i)}
              >
                <div className={cn("flex flex-1 items-end justify-center gap-[2px] rounded-sm px-[10%]", active && "bg-muted/60")}>
                  <div
                    className="w-full max-w-5 rounded-t bg-series-current"
                    style={{ height: h(r.amount) }}
                  />
                  {showPrev && (
                    <div className="w-full max-w-5 rounded-t bg-series-prev" style={{ height: h(r.prevAmount) }} />
                  )}
                </div>
                <div className="h-5 pt-1 text-center text-[10px] text-muted-foreground">{MONTHS[r.month - 1]}</div>
                {active && (
                  <div
                    className={cn(
                      "pointer-events-none absolute bottom-full z-10 mb-1 w-44 rounded-lg border border-border bg-popover p-2 text-xs shadow-md",
                      i < rows.length / 2 ? "left-0" : "right-0"
                    )}
                  >
                    <div className="mb-1 font-medium">
                      {MONTHS[r.month - 1]} {r.year}
                    </div>
                    <TooltipRow swatch="bg-series-current" label={currentName} value={formatAmount(r.amount)} />
                    <div className="pl-4 text-muted-foreground">{r.count} donations</div>
                    {showPrev && prevName && (
                      <TooltipRow swatch="bg-series-prev" label={prevName} value={formatAmount(r.prevAmount)} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TooltipRow({ swatch, label, value }: { swatch: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn("size-2.5 shrink-0 rounded-sm", swatch)} />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-medium tabular-nums">{value}</span>
    </div>
  );
}

function MonthlyTable({ rows, currentName, prevName }: { rows: MonthRow[]; currentName: string; prevName?: string }) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const prevTotal = rows.reduce((s, r) => s + r.prevAmount, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Month</th>
            <th className="py-2 pr-3 text-right font-medium">Donations</th>
            <th className="py-2 pr-3 text-right font-medium">{currentName}</th>
            {prevName && <th className="py-2 text-right font-medium">{prevName}</th>}
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.map((r) => (
            <tr key={`${r.year}-${r.month}`} className="border-b border-border/60">
              <td className="py-1.5 pr-3">
                {MONTHS[r.month - 1]} {r.year}
              </td>
              <td className="py-1.5 pr-3 text-right">{r.count}</td>
              <td className="py-1.5 pr-3 text-right">{formatAmount(r.amount)}</td>
              {prevName && <td className="py-1.5 text-right text-muted-foreground">{formatAmount(r.prevAmount)}</td>}
            </tr>
          ))}
          <tr className="font-medium">
            <td className="py-2 pr-3">Total</td>
            <td className="py-2 pr-3 text-right">{rows.reduce((s, r) => s + r.count, 0)}</td>
            <td className="py-2 pr-3 text-right">{formatAmount(total)}</td>
            {prevName && <td className="py-2 text-right text-muted-foreground">{formatAmount(prevTotal)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------ breakdowns

function BreakdownCard({ title, description, rows }: { title: string; description: string; rows: Breakdown[] }) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const max = Math.max(0, ...rows.map((r) => r.amount));
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty />
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((r) => (
              <li key={`${r.id ?? "none"}-${r.name}`} className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">{r.name}</span>
                  <span className="shrink-0 font-medium tabular-nums">{formatAmount(r.amount)}</span>
                  <span className="w-10 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {total > 0 ? `${Math.round((r.amount / total) * 100)}%` : ""}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-muted" title={`${r.count} donations`}>
                  <div
                    className="h-full rounded-full bg-series-current"
                    style={{ width: `${max > 0 ? (r.amount / max) * 100 : 0}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TopDonorsCard({ rows, onOpenDonor }: { rows: DashboardData["topDonors"]; onOpenDonor: (id: number) => void }) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Top donors</CardTitle>
        <CardDescription>Top 10 by amount received this financial year</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty />
        ) : (
          <ol className="divide-y divide-border">
            {rows.map((r, i) => (
              <li key={r.id ?? i} className="flex items-center gap-3 py-2 text-sm">
                <span className="w-5 shrink-0 text-right text-xs text-muted-foreground tabular-nums">{i + 1}</span>
                <DonorLink
                  name={r.name || "(unnamed)"}
                  donorId={r.id}
                  onOpen={onOpenDonor}
                  className="min-w-0 flex-1"
                />
                <span className="shrink-0 text-xs text-muted-foreground">{r.count}×</span>
                <span className="w-24 shrink-0 text-right font-medium tabular-nums">{formatAmount(r.amount)}</span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function RecentCard({ data, onOpenDonor }: { data: DashboardData; onOpenDonor: (id: number) => void }) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Recent donations</CardTitle>
        <CardDescription>
          {data.context.cause
            ? `Latest 10 receipts for ${data.context.cause.name} (amount for this cause)`
            : data.context.department
              ? `Latest 10 receipts for ${data.context.department.name} (amount for its causes)`
              : "Latest 10 receipts in this operation unit"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.recent.length === 0 ? (
          <Empty />
        ) : (
          <ul className="divide-y divide-border">
            {data.recent.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <DonorLink
                    name={r.nameOnReceipt || r.donorName || "(no name)"}
                    donorId={r.donorId}
                    onOpen={onOpenDonor}
                    className="block"
                  />
                  <div className="truncate text-xs text-muted-foreground">
                    {[r.receiptNumber, formatDate(r.receiptDate), r.modeOfPayment].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-medium tabular-nums">{formatAmount(r.amount)}</span>
                  <Badge variant="outline" className="gap-1 text-[10px]">
                    {r.received ? "Received" : (
                      <>
                        <Clock className="size-3" /> Pending
                      </>
                    )}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-6 text-center text-sm text-destructive">{message}</CardContent>
    </Card>
  );
}

function Empty() {
  return <p className="py-6 text-center text-sm text-muted-foreground">No donations in this period.</p>;
}
